export type WechatJsSdkSignature = {
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
};

type WechatConfig = {
  debug: boolean;
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
  openTagList: string[];
};

type WechatJsSdk = {
  config: (config: WechatConfig) => void;
  ready: (callback: () => void) => void;
  error: (callback: (error: unknown) => void) => void;
};

declare global {
  interface Window {
    wx?: WechatJsSdk;
  }
}

const WECHAT_SDK_SRC = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';

export function isWechatBrowser(userAgent = window.navigator.userAgent) {
  return /micromessenger/i.test(userAgent);
}

export function getWechatShareUrl() {
  return window.location.href.split('#')[0];
}

export async function loadWechatSdk() {
  if (window.wx) return window.wx;

  const existing = document.querySelector<HTMLScriptElement>(`script[src="${WECHAT_SDK_SRC}"]`);
  if (existing) {
    await waitForWechatSdk();
    return window.wx;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WECHAT_SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load WeChat JS SDK'));
    document.head.appendChild(script);
  });

  await waitForWechatSdk();
  return window.wx;
}

async function waitForWechatSdk() {
  await new Promise<void>((resolve, reject) => {
    let attempts = 0;
    const timer = window.setInterval(() => {
      if (window.wx) {
        window.clearInterval(timer);
        resolve();
        return;
      }

      attempts += 1;
      if (attempts >= 50) {
        window.clearInterval(timer);
        reject(new Error('WeChat JS SDK did not become available'));
      }
    }, 100);
  });
}

export async function fetchWechatSignature(signatureUrl: string) {
  const url = new URL(signatureUrl, window.location.origin);
  url.searchParams.set('url', getWechatShareUrl());

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch WeChat signature: ${response.status}`);
  }

  return response.json() as Promise<WechatJsSdkSignature>;
}
